import type { BooleanResolver } from '@pct-ts/typing';
import { isBoolean } from '@pct-ts/typeguard';

/**
 * Resolves a boolean value based on a resolver function or a static boolean.
 *
 * This function accepts either a boolean directly or a function that returns a boolean.
 * If a boolean is provided, it's returned as-is. If a function is provided, it's called
 * with the given parameter to generate the boolean value.
 *
 * @param resolver - A boolean value or a function that resolves to a boolean
 * @param param - The parameter to pass to the resolver function
 * @returns The resolved boolean value
 *
 * @example
 * // Using a static boolean
 * const static = resolveBoolean(true, {});
 * console.log(static); // Output: true
 *
 * @example
 * // Using a resolver function
 * const dynamic = resolveBoolean(
 *   (user: { isAdmin: boolean }) => user.isAdmin,
 *   { isAdmin: true }
 * );
 * console.log(dynamic); // Output: true
 */
export function resolveBoolean<T>(resolver: BooleanResolver<T>, param: T): boolean {
  if (isBoolean(resolver)) {
    return resolver;
  }
  return resolver(param);
}
